/**
 * URL-backed master-toggle filter for the GHL middleware location list.
 *
 * Mirrors the pattern in `useUrlTimeRange`: state lives in the URL search
 * string so refresh, share, and browser back-button all preserve filter
 * selections. The setter merges with existing params (preserves the time
 * range and any other URL state) and uses `{ replace: true }` so filter
 * changes don't pollute history.
 *
 * URL params:
 *   ?crm=all|on|off       (default: all)
 *
 * Tri-state was chosen over boolean checkboxes because operators need to be
 * able to inspect EITHER cohort (locations with the toggle ON, or those
 * with it OFF) — a checkbox can express "filter active" but not direction.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

export type ToggleFilter = 'all' | 'on' | 'off'

export interface LocationFilters {
  readonly crm: ToggleFilter
}

const DEFAULT_FILTERS: LocationFilters = {
  crm: 'all',
}

function parseToggleFilter(value: string | null): ToggleFilter {
  if (value === 'on' || value === 'off') return value
  return 'all'
}

export function useUrlLocationFilters(): [
  LocationFilters,
  (next: Partial<LocationFilters>) => void,
] {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo<LocationFilters>(
    () => ({
      crm: parseToggleFilter(searchParams.get('crm')),
    }),
    [searchParams]
  )

  const setFilters = useCallback(
    (next: Partial<LocationFilters>) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev)
          const merged = { ...filters, ...next }

          // Strip "all" rather than serialize it — keeps URLs clean when
          // filters are at their defaults. Other filter values get an
          // explicit param so the URL is self-describing.
          if (merged.crm === 'all') params.delete('crm')
          else params.set('crm', merged.crm)

          return params
        },
        { replace: true }
      )
    },
    [filters, setSearchParams]
  )

  return [filters, setFilters]
}

/**
 * Apply the active filters to a location array.
 *
 * Pulled out as a pure function so the same filtering logic can drive the
 * cards view, the table view, and the grand-total computation without
 * duplicating the predicate.
 */
export function applyLocationFilters<T extends {
  readonly crm_automation_enabled: boolean
}>(locations: readonly T[], filters: LocationFilters): T[] {
  return locations.filter((loc) => {
    if (filters.crm === 'on' && !loc.crm_automation_enabled) return false
    if (filters.crm === 'off' && loc.crm_automation_enabled) return false
    return true
  })
}

export { DEFAULT_FILTERS }
